<script setup>

    import { onMounted, ref } from 'vue'

    import LatteStyledContainer from '@/Components/App/LatteStyledContainer.vue'

    import { useRoute } from 'vue-router'
    import { util } from '@/Helpers'
    import WeekPicker from '@/Components/WeekPicker.vue'
    import LatteCaregiverSchedule from '@/Components/Caregiver/LatteCaregiverSchedule.vue'
    import LatteCaregiverServiceTypesEditor from '@/Components/Caregiver/LatteCaregiverServiceTypesEditor.vue'
    import { useCaregiverSingleStore, useNotificationStore } from '@/Stores'
    import { storeToRefs } from 'pinia'
    import { useJobRunner } from '@/Helpers/jobRunner'

    const notificationStore = useNotificationStore()
    const caregiverSingleStore = useCaregiverSingleStore()

    const { caregiver } = storeToRefs(caregiverSingleStore)

    const caregiverJobRunner = useJobRunner('caregiver')
    caregiverJobRunner.subscribe('addedRetryJob', function(errorCode, retryTime) {
        notificationStore.add('Error Saving Changes', 'error', `Retrying automatically... (${retryTime}/20)`, 15000)
        caregiverSingleStore.stopInactivityTimeout()
    })
    caregiverJobRunner.subscribe('finishedJobSuccess', function() {
        caregiverSingleStore.resetInactivityRefreshTimer()
    })
    caregiverJobRunner.subscribe('addedJob', function() {
        caregiverSingleStore.resetInactivityRefreshTimer()
        notificationStore.removeById(caregiverSingleStore.getMaxRetryDialogId())
        caregiverSingleStore.removeMaxRetryDialogId()
    })
    caregiverJobRunner.subscribe('reachedMaxRetry', function() {
        const maxRetryDialogId = notificationStore.add('Error Saving Changes', 'error', 'Make an edit to try again.', 7 * 24 * 60 * 60 * 1000)
        caregiverSingleStore.setMaxRetryDialogId(maxRetryDialogId)
    })

    const scheduleDate = ref(util.date.lastSunday())
    const route = useRoute()
    const loading = ref(true)

    async function hydrateCaregiver(data) {
        await caregiverSingleStore.hydrate(data)
        loading.value = false
    }

    function updateCaregiver(data, formId) {
        caregiverSingleStore.applyLocalChangeToActiveCaregiver(data)
        caregiverJobRunner.addJob('update', caregiver.value.id, data, [formId], async (data, formId, jobId) => {
            await caregiverSingleStore.updateCaregiver(caregiver.value.id, data, formId, jobId)
                .then(function (response) {
                    if (caregiverJobRunner.hasScheduleJob('update', caregiver.value.id) == false) {
                        hydrateCaregiver(response.data)
                    }
                    caregiverJobRunner.handleJobSuccess('update', caregiver.value.id, jobId)
                    notificationStore.add('Changes Saved')
                })
                .catch(function(error) {
                    caregiverJobRunner.handleJobError('update', caregiver.value.id, jobId, error.error.response?.status)
                })
        })
    }

    onMounted(async () => {
        const caregiver_id = route.params.caregiver
        caregiverSingleStore.getCaregiverById(caregiver_id, 'caregiver')
            .then(response => {
                caregiverSingleStore.hydrate(response.data)
            })
    })

    const scheduleBuilderMode = ref(null)
</script>

<template>

    <div class="mx-auto max-w-7x md:px-8 sm:pt-4 md:pt-6">

        <div class="mx-auto max-w-5xl">

            <LatteStyledContainer
                id="basic_info"
                title="Basic Info"
                :collapsible="true"
                :forceSingleLineMobileLayout="true"
            >
                <template #default>
                    <LatteCaregiverServiceTypesEditor
                        @caregiver:update="updateCaregiver"
                        @caregiver:cancel-update="() => {}"
                    />
                </template>
            </LatteStyledContainer>

            <LatteStyledContainer
                id="schedule"
                title="Schedule"
                :collapsible="true"
                :forceSingleLineMobileLayout="true"
            >
                <template #header>
                    <div class="w-full flex justify-center items-center">
                        <WeekPicker
                            v-model="scheduleDate"
                            :disabled="scheduleBuilderMode == 'edit'"
                        />
                    </div>
                </template>
                <template #default>
                    <LatteCaregiverSchedule
                        :caregiver_id="parseInt(route.params.caregiver)"
                        :schedule_start_date="scheduleDate"
                        @builder-mode:change="(mode) => scheduleBuilderMode = mode"
                        @caregiver:change="updateCaregiver"
                    />
                </template>
            </LatteStyledContainer>
        </div>
    </div>

</template>
